import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AngularFirestore, AngularFirestoreCollection, AngularFirestoreDocument } from 'angularfire2/firestore';
import { FirestoreService } from './firestore/firestore.service';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/combineLatest';
import 'rxjs/add/operator/mergeMap';

import { Globals } from '../globals';

export interface Ship {
  active: number;
  createdAt: any;
  description: string;
  lines: number;
  name: string;
  sizeHa: number;
  subsidiaryId: string;
  supervisorId: string;
  updatedAt: any;
}

export interface ShipId extends Ship {
  id: string;
}

@Injectable()
export class ShipService {
  shipCollection: AngularFirestoreCollection<Ship>;
  ships: any;

  shipDocument: AngularFirestoreDocument<ShipId>;
  ship: Observable<ShipId>;

  collection: string;
  subsidiaryRef: string;
  employeeRef: string;

  // NOTE: Change this any for Ship Interface
  ship$: Observable<any>;
  ships$: Observable<any[]>;

  constructor(
    private afs: AngularFirestore,
    private afh: FirestoreService,
    private router: Router,
  ) {
    this.collection = Globals.SHIP_COLLECTION;
    this.subsidiaryRef = Globals.SUBSIDIARY_COLLECTION;
    this.employeeRef = Globals.EMPLOYEE_COLLECTION;
  }

  postShip(shipObject: any) {
    this.shipCollection = this.afs.collection(this.collection);

    return this.shipCollection.add(shipObject).then(function(docRef) {
      // console.log('Document added with ID: ', docRef.id);
    }).catch(function(error) {
      // console.error('Error adding document: ', error);
    });
  }

  postShipWithId(shipObject: any, id$: string) {
    this.shipCollection = this.afs.collection(this.collection);

    return this.shipCollection.doc(id$).set(shipObject).then(function(docRef) {
      // console.log('Document added with ID: ', docRef.id);
    }).catch(function(error) {
      // console.error('Error adding document: ', error);
    });
  }

  /**
   * Get all ships with references
   * @return {Observable} Arrays of ships
   */
  getShips({onEmpty=undefined}={}) {
    return this.afh.colWithIds$(this.collection, ref => ref.orderBy('name').where('active', '==', true) ).map(documents => {
      if(!documents.length && onEmpty) onEmpty();
      return documents.map(document => {
        const supervisor = this.afh.doc$(`${this.employeeRef}/${document.supervisorId}`);
        const subsidiary = this.afh.doc$(`${this.subsidiaryRef}/${document.subsidiaryId}`);
        return Observable.combineLatest(
          supervisor,
          subsidiary,
          (supervisorData, subsidiaryData) => {
            return {
              supervisor: supervisorData,
              subsidiary: subsidiaryData
            };
        })
        .map(data => {
          return { supervisorObj: data.supervisor, subsidiaryObj: data.subsidiary, ...document };
        });
      });
    })
    .mergeMap(shipsDocuments => Observable.combineLatest(shipsDocuments));
  }

  /**
  * Get ship data
  * @param {string} sid - The id of the ship
  * @return {Observable} The Ship Object
  */
  getShip(sid: string) {
    return this.ship$ = this.afh.docWithIds$(this.collection + '/' + sid).map(document => {
      const supervisor = this.afh.doc$(`${this.employeeRef}/${document.supervisorId}`);
      const subsidiary = this.afh.doc$(`${this.subsidiaryRef}/${document.subsidiaryId}`);
      return Observable.combineLatest(
        supervisor,
        subsidiary,
        (supervisorData, subsidiaryData) => {
          return {
            supervisor: supervisorData,
            subsidiary: subsidiaryData
          };
      })
      .map(data => {
        return { supervisorObj: data.supervisor, subsidiaryObj: data.subsidiary, ...document };
      });
    })
    .mergeMap(ship => Observable.combineLatest(ship));
  }

  /**
  * Create new ship
  * @param {Ship} ship - The data of the ship
  */
  addShip(ship: Ship) {
    this.afh.add(`${this.collection}`, ship);
    this.router.navigate(['/ship']);
  }

  /**
   * Update current ship
   * @param {string} sid  - The id of the ship
   * @param {Ship} ship - The data of the ship
   */
  updateShip(sid: string, ship: Ship) {
    this.afh.update(`${this.collection}/${sid}`, ship);
    this.router.navigate(['/ship']);
  }

  /**
   * Delete current ship
   * @param {string} sid - The id of the ship
   */
  deleteShip(sid: string) {
    this.afh.delete(`${this.collection}/${sid}`);
  }
}
